import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { SeoHead } from "@/components/SeoHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadConfirm() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("loading"); // loading | confirmed | error
  const [lead, setLead] = useState(null);
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) { setState("error"); setError("This confirmation link is missing its token."); return; }
    (async () => {
      try {
        const { data } = await axios.post(`${API}/lead/confirm`, { token });
        setLead(data);
        setState("confirmed");
      } catch (err) {
        setError(err.response?.data?.detail || "This confirmation link is invalid or has expired.");
        setState("error");
      }
    })();
  }, [token]);

  return (
    <div data-testid="lead-confirm-root" className="relative min-h-screen w-full bg-background text-foreground">
      <SeoHead title="Confirm your request — Sema" description="Confirm your Sema request." path="/lead/confirm" />
      <MarketingNav />
      <main className="mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center px-6 text-center">
        <div className="w-full rounded-2xl border border-border/60 bg-card/60 p-10 backdrop-blur">
          {state === "loading" && (
            <div data-testid="lead-confirm-loading" className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
              <p className="text-sm text-muted-foreground">Confirming your request…</p>
            </div>
          )}
          {state === "confirmed" && (
            <div data-testid="lead-confirm-success" className="flex flex-col items-center gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-accent/40 bg-accent/15 text-accent">
                <Check className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <h1 className="text-2xl font-semibold tracking-tight">You're confirmed{lead?.name ? `, ${lead.name}` : ""}.</h1>
              <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
                Thanks{lead?.company ? ` from ${lead.company}` : ""} — our team will reach out within one business day. Want to look around first?
              </p>
              <div className="mt-2 flex flex-wrap justify-center gap-3">
                <Link to="/signup">
                  <Button className="rounded-full bg-accent px-5 font-mono text-xs uppercase tracking-[0.2em] text-accent-foreground hover:bg-accent/90">
                    Start free sandbox <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/product">
                  <Button variant="outline" className="rounded-full border-border/70 px-5 font-mono text-xs uppercase tracking-[0.2em]">
                    See how it works
                  </Button>
                </Link>
              </div>
            </div>
          )}
          {state === "error" && (
            <div data-testid="lead-confirm-error" className="flex flex-col items-center gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-destructive/40 bg-destructive/15 text-destructive">
                <AlertTriangle className="h-6 w-6" />
              </span>
              <h1 className="text-2xl font-semibold tracking-tight">Link expired or invalid</h1>
              <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">{error}</p>
              <Link to="/demo" className="mt-2">
                <Button variant="outline" className="rounded-full border-border/70 px-5 font-mono text-xs uppercase tracking-[0.2em]">
                  Submit again
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
